// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardHeader,
  CardContent,
  List,
  ListItem,
  Divider,
  CardMedia,
  styled,
  Tooltip,
  IconButton,
  Skeleton,
} from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  CertificateHolder,
  GetLeaderboardRequestState,
  LeaderboardResponse,
} from "../../utils/types";
import TrophyIcon from "../../assets/img/trophy.svg";

const StyledListItem = styled(ListItem)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0.75),
  marginBottom: theme.spacing(1),
  borderRadius: 10,
  border: `1px solid ${theme.palette.divider}`,
  transition: "all 0.3s ease",
  textTransform: "capitalize",
}));

const OverlayBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(255, 255, 255, 0.7)",
  zIndex: 1,
}));

const Leaderboard: React.FC<{ data: GetLeaderboardRequestState }> = ({
  data,
}) => {
  const [title, setTitle] = React.useState<string>("Leaderboard");
  
  useEffect(() => {
    if (!data.backgroundProcess) {
      const response = data.response as LeaderboardResponse;
      if (response.leaderboardQuarter) {
        setTitle(
          `Leaderboard (${response.leaderboardQuarter.quarter} ${response.leaderboardQuarter.year})`
        );
      }
    }
  }, [data]);

  const getRankColour = (index: number) => {
    switch (index) {
      case 0:
        return "#FFD700";
      case 1:
        return "#C0C0C0";
      case 2:
        return "#CD7F32";
      default:
        return "inherit";
    }
  };

  const renderLeaderboardItem = (
    holder: CertificateHolder,
    index: number,
    isTopHolder: boolean = false
  ) => (
    <StyledListItem key={holder.certificationId}>
      <Box
        sx={{
          width: 24,
          height: 24,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          ml: 1.5,
          mr: 2,
          fontSize: 18,
          fontWeight: "bold",
          color: isTopHolder ? "#FFD700" : getRankColour(index),
        }}
      >
        {isTopHolder ? 1 : index + 1}
      </Box>
      <Box sx={{ flexGrow: 1, textAlign: "left" }}>
        <Typography variant="body2" fontWeight="light">
          {holder.fullName}
        </Typography>
      </Box>
      <Typography
        variant="body2"
        fontWeight="bold"
        color="primary"
        sx={{
          pr: 2,
        }}
      >
        {holder.marks} {holder.marks === 1 ? "pt" : "pts"}
      </Typography>
    </StyledListItem>
  );

  const renderQuarterlyWinnerLeaderboardItem = (
    holder: CertificateHolder,
    index: number,
    isTopHolder: boolean = false
  ) => (
    <StyledListItem key={holder.certificationId}>
      {isTopHolder ? (
        <EmojiEventsIcon
          sx={{ color: getRankColour(0), ml: 1.5, mr: 2, fontSize: 24 }}
        />
      ) : (
        <EmojiEventsIcon
          sx={{ color: getRankColour(index), ml: 1.5, mr: 2, fontSize: 24 }}
        />
      )}
      <Box sx={{ flexGrow: 1, textAlign: "left" }}>
        <Typography variant="body2" fontWeight="light">
          {holder.fullName}
        </Typography>
      </Box>
      <Typography
        variant="body2"
        fontWeight="bold"
        color="primary"
        sx={{
          pr: 2,
        }}
      >
        {holder.marks} {holder.marks === 1 ? "pt" : "pts"}
      </Typography>
    </StyledListItem>
  );

  const renderSkeletonItem = () => (
    <StyledListItem>
      <Skeleton
        variant="circular"
        width={24}
        height={24}
        sx={{ ml: 1.5, mr: 2 }}
      />
      <Box sx={{ flexGrow: 1, textAlign: "left" }}>
        <Skeleton variant="text" width="60%" />
      </Box>
      <Skeleton variant="text" width={40} sx={{ pr: 2 }} />
    </StyledListItem>
  );

  const pointsCalculationTooltip = (
    <Box>
      <Typography variant="body2">
        Points are calculated based on certification level:
      </Typography>
      <Typography variant="body2">
        • Expert & Solutions Architect: 30 points
      </Typography>
      <Typography variant="body2">
        • Developer and Sales Professional: 20 points
      </Typography>
      <Typography variant="body2">• Practitioner: 10 points</Typography>
      <Typography variant="body2">
          In the event of a tie, the marks received across all exams passed during the specified quarter will be compared, and the winner will be determined based on the highest average score.
      </Typography>
    </Box>
  );

  const renderSkeletonWithOverlay = () => (
    <Box sx={{ position: "relative" }}>
      <List disablePadding>
        {[...Array(5)].map((_, index) => (
          <React.Fragment key={index}>{renderSkeletonItem()}</React.Fragment>
        ))}
      </List>
      <OverlayBox>
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight="medium"
        >
          {data.state.charAt(0).toUpperCase() + data.state.slice(1)}
        </Typography>
      </OverlayBox>
    </Box>
  );

  return (
    <Card
      sx={{
        p: 3,
        pb: 0,
        mb: 3,
        height: "463.52px",
        width: "100%"
      }}
    >
      <CardMedia
        component="img"
        sx={{ width: 100, ml: 2 }}
        image={TrophyIcon}
      />
      <CardHeader
        title={title}
        titleTypographyProps={{
          align: "left",
        }}
        subheaderTypographyProps={{
          align: "right",
        }}
      />
      <Divider variant="middle" />

      <CardContent
        sx={{
          mt: 1,
          mb: data.response.previousQuarterLeaderboard?.topCertificateHolder
            ? 0
            : 7,
        }}
      >
        {data.backgroundProcess || data.state !== "success" ? (
          renderSkeletonWithOverlay()
        ) : (
          <List disablePadding>
            {data.response.leaderboardQuarter?.topCertificateHolders?.map(
              (holder, index) => renderLeaderboardItem(holder, index)
            )}
          </List>
        )}
        {data.response.previousQuarterLeaderboard?.topCertificateHolder && (
          <>
            <Typography
              variant="subtitle2"
              color="secondary.main"
              my={2}
              textTransform={"uppercase"}
              textAlign={"center"}
            >
              <Typography
                variant="subtitle2"
                color="secondary.main"
                fontWeight="bold"
              >
                Quarterly Winner {" "}
                {data.response.previousQuarterLeaderboard?.quarter}{" "}
                {data.response.previousQuarterLeaderboard?.year}
              </Typography>
            </Typography>
            {data.backgroundProcess ? (
              <List disablePadding>{renderSkeletonItem()}</List>
            ) : (
              <List disablePadding>
                {data.response.previousQuarterLeaderboard
                  ?.topCertificateHolder &&
                  renderQuarterlyWinnerLeaderboardItem(
                    data.response.previousQuarterLeaderboard
                      .topCertificateHolder,
                    0,
                    true
                  )}
              </List>
            )}
          </>
        )}
        <Box
          color="text.primary"
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            mt: 0,
          }}
        >
          <Typography variant="caption" sx={{ mr: 1 }} color="text.primary">
            How are points calculated?
          </Typography>
          <Tooltip title={pointsCalculationTooltip} arrow placement="top">
            <IconButton size="small" color="inherit">
              <InfoOutlinedIcon fontSize="small" color="inherit" />
            </IconButton>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );
};

export default Leaderboard;
